import { useEffect, useState } from "react";
import { FiArrowUpRight, FiLoader, FiXCircle } from "react-icons/fi";
import { Link } from "react-router";
import { apiFetch } from "../utils/apiFetch";
import CancelMembershipPopup from "./CancelMembershipPopup";
import { ownerPlan } from "../utils/communityEntitlements.mjs";

export default function SubscriptionSettings() {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [cancellation, setCancellation] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const ended = ["cancelled", "completed", "expired"].includes(subscription?.status);
  const currentPlan = { default: "Free", plus: "Plus", pro: "Pro" }[ownerPlan(subscription)];
  const pending = ["requested", "confirmed", "unconfirmed"].includes(cancellation?.state);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const load = async () => {
      try {
        const response = await apiFetch(`${process.env.API_URL}/api/subscriptions/me`, {
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Could not load membership");
        if (!controller.signal.aborted) {
          setSubscription(data.subscription);
          setCancellation(data.cancellation);
        }
      } catch (error) {
        if (!controller.signal.aborted) setError(error.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, []);

  const cancel = async () => {
    if (busy || pending || ended || !subscription?.providerSubscriptionId) return;
    setBusy(true);
    setError("");
    try {
      const response = await apiFetch(`${process.env.API_URL}/api/subscriptions/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subscriptionId: subscription.providerSubscriptionId }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.code === "CANCELLATION_UNCONFIRMED") {
          setCancellation({ state: "unconfirmed" });
          setShowPopup(false);
        }
        throw new Error(data.message || "Could not cancel membership");
      }
      setCancellation(data.cancellation || null);
      if (data.action === "subscription_ended") setSubscription(null);
      setNotice(data.message);
      setShowPopup(false);
    } catch (error) {
      setError(error.message);
    } finally {
      setBusy(false);
    }
  };

  return <section aria-label="Platform subscription" className="max-w-xl space-y-4">
    {!loading && !error && <p className="font-medium">You are on {currentPlan.toLowerCase()} plan</p>}
    {!loading && !error && currentPlan !== "Pro" && <div className="space-y-3">
      {currentPlan === "Free" && <p className="text-sm text-gray-600">Upgrade to host videos and offer premium memberships in your channels.</p>}
      {currentPlan === "Plus" && <p className="text-sm text-gray-600">Upgrade for more features</p>}
      <Link to="/choose-plan" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-orange-700 px-4 py-2 text-sm font-semibold text-white hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2">Upgrade plan<FiArrowUpRight aria-hidden="true" /></Link>
    </div>}
        {loading ? <div role="status" aria-label="Loading membership" className="flex h-10 items-center justify-center"><FiLoader className="h-5 w-5 animate-spin" /></div>
          : currentPlan !== "Free" && subscription?.providerSubscriptionId && !ended && !pending && <button type="button"
            onClick={() => { setError(""); setNotice(""); setShowPopup(true); }}
            className="flex min-h-10 w-full items-center gap-2 rounded-md px-2 text-left text-red-700 hover:bg-red-50">
            <FiXCircle aria-hidden="true" className="h-4 w-4 shrink-0" />Cancel membership
          </button>}
        {!loading && pending && !ended && <p role="status" className="my-2 text-xs leading-5 text-gray-600">
          {cancellation.state !== "confirmed" ? "Cancellation confirmation is pending. Contact support before retrying."
            : cancellation.atCycleEnd && cancellation.effectiveAt
              ? `Membership continues until ${new Date(cancellation.effectiveAt).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}. Renewal is cancelled.`
              : "Membership cancelled."}
        </p>}
        {notice && <p role="status" className="my-2 text-xs text-orange-700">{notice}</p>}
        {error && !showPopup && <p role="alert" className="my-2 text-xs text-red-700">{error}</p>}

      {showPopup && <CancelMembershipPopup endDate={subscription?.currentPeriodEnd} busy={busy}
        error={error} onClose={() => setShowPopup(false)} onConfirm={cancel} />}
  </section>;
}
